/**
 * Cron day-of-week field ⇄ a set of weekdays (#2191).
 *
 * Shared by the Schedule form (emit + parse back) and describeCron (render),
 * so the three never disagree about what a field like `2-6` or `1,3,5` means.
 * Days are numbered as cron does: 0 = Sunday … 6 = Saturday. `7` (Sunday,
 * which croniter also accepts) and three-letter names (`MON`, `tue`) are read
 * so a schedule set through the API still parses; steps (`1-5/2`) are not.
 */

export const ALL_DAYS: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

export const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const NAME_TO_DAY: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};

/** One day token → 0–7 (7 kept so a range ending at 7 expands correctly). */
function parseDayToken(token: string): number | null {
  if (/^\d$/.test(token)) {
    const n = Number(token);
    return n <= 7 ? n : null;
  }
  const named = NAME_TO_DAY[token.toLowerCase()];
  return named === undefined ? null : named;
}

/**
 * Parse a cron day-of-week field into sorted, de-duplicated days 0–6.
 * `*` is every day. Returns null for anything the form cannot represent.
 */
export function parseDayOfWeekField(field: string): number[] | null {
  if (field === '*') return [...ALL_DAYS];
  const days = new Set<number>();
  for (const item of field.split(',')) {
    const bounds = item.split('-');
    if (bounds.length > 2) return null;
    const start = parseDayToken(bounds[0]);
    const end = bounds.length === 2 ? parseDayToken(bounds[1]) : start;
    if (start === null || end === null || start > end) return null;
    for (let d = start; d <= end; d++) days.add(d % 7);
  }
  return [...days].sort((a, b) => a - b);
}

/** Split sorted days into runs of consecutive days: [1,2,3,5] → [[1,2,3],[5]]. */
function consecutiveRuns(days: number[]): number[][] {
  const runs: number[][] = [];
  for (const d of days) {
    const last = runs[runs.length - 1];
    if (last && d === last[last.length - 1] + 1) last.push(d);
    else runs.push([d]);
  }
  return runs;
}

function normalize(days: readonly number[]): number[] {
  return [...new Set(days)].filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b);
}

/**
 * Days → a cron day-of-week field. Every day is `*`, so a Daily schedule with
 * all seven ticked emits exactly the cron it always did. Three or more
 * consecutive days become a range (`2-6`); shorter runs stay a list (`0,6`).
 */
export function encodeDaysOfWeek(days: readonly number[]): string {
  const sorted = normalize(days);
  if (sorted.length === 7) return '*';
  return consecutiveRuns(sorted)
    .map((run) => (run.length >= 3 ? `${run[0]}-${run[run.length - 1]}` : run.join(',')))
    .join(',');
}

/** Days → short label: "Tue–Sat", "Mon, Wed, Fri", "Mon–Wed, Fri". */
export function formatDaysOfWeek(days: readonly number[]): string {
  return consecutiveRuns(normalize(days))
    .map((run) =>
      run.length >= 3
        ? `${SHORT_DAY_NAMES[run[0]]}–${SHORT_DAY_NAMES[run[run.length - 1]]}`
        : run.map((d) => SHORT_DAY_NAMES[d]).join(', '),
    )
    .join(', ');
}
